import { z } from "zod";

export const SURVEY_QUESTIONS = [
  "The safety devices meet the practical needs of our frontline or lone workers.",
  "The devices perform consistently in day-to-day use across our teams.",
  "When an emergency has been declared, we were confident our people would get the help they needed.",
  "When we've needed help, the support team has responded in a timely and helpful way.",
  "I trust the devices to work reliably when our people need them most.",
  "The devices are easy for our teams to understand and use correctly.",
  "Our team feels safer since implementing Duress devices.",
  "The service and support we receive gives us confidence in continuing to work with Duress.",
] as const;

export const SURVEY_SCALE_LABELS: Record<number, string> = {
  1: "Strongly Disagree",
  2: "Disagree",
  3: "Neutral",
  4: "Agree",
  5: "Strongly Agree",
};

const scoreField = z.number().int().min(1).max(5);

export const SurveySendStatusSchema = z.enum(["pending", "completed"]);
export type SurveySendStatus = z.infer<typeof SurveySendStatusSchema>;

export const SurveySendSchema = z.object({
  id: z.string().uuid(),
  clientId: z.string().uuid(),
  clientName: z.string(),
  token: z.string(),
  sentAt: z.string().datetime({ offset: true }),
  sentByUserName: z.string(),
  status: SurveySendStatusSchema,
  sentToContacts: z.array(z.object({ name: z.string(), email: z.string() })),
});
export type SurveySend = z.infer<typeof SurveySendSchema>;

export const SurveyResponseSchema = z.object({
  id: z.string().uuid(),
  surveyId: z.string().uuid(),
  clientId: z.string().uuid(),
  completedAt: z.string().datetime({ offset: true }),
  respondentName: z.string().nullable(),
  comments: z.string().nullable(),
  scores: z.tuple([
    scoreField, scoreField, scoreField, scoreField,
    scoreField, scoreField, scoreField, scoreField,
  ]),
  averageScore: z.number(),
});
export type SurveyResponse = z.infer<typeof SurveyResponseSchema>;

export const SurveySendWithResponseSchema = SurveySendSchema.extend({
  response: SurveyResponseSchema.nullable(),
});
export type SurveySendWithResponse = z.infer<typeof SurveySendWithResponseSchema>;

export const SurveySendListResponseSchema = z.object({
  items: z.array(SurveySendWithResponseSchema),
});
export type SurveySendListResponse = z.infer<typeof SurveySendListResponseSchema>;

// Request to create a new survey send
export const CreateSurveySendRequestSchema = z.object({
  contactIds: z.array(z.string().uuid()).min(1),
});
export type CreateSurveySendRequest = z.infer<typeof CreateSurveySendRequestSchema>;

// Public: what the form page fetches before showing the survey
export const PublicSurveyInfoSchema = z.object({
  clientName: z.string(),
  alreadyCompleted: z.boolean(),
});
export type PublicSurveyInfo = z.infer<typeof PublicSurveyInfoSchema>;

// Public: submitted by the form
export const SubmitSurveyRequestSchema = z.object({
  respondentName: z.string().max(200).nullable(),
  comments: z.string().max(5000).nullable(),
  scores: z.tuple([
    scoreField, scoreField, scoreField, scoreField,
    scoreField, scoreField, scoreField, scoreField,
  ]),
});
export type SubmitSurveyRequest = z.infer<typeof SubmitSurveyRequestSchema>;

// Aggregate results
export const SurveyAggregateSchema = z.object({
  totalSent: z.number().int(),
  totalCompleted: z.number().int(),
  averageScoreOverall: z.number().nullable(),
  averageScorePerQuestion: z.array(z.number().nullable()),
  recentResponses: z.array(
    z.object({
      clientId: z.string().uuid(),
      clientName: z.string(),
      completedAt: z.string().datetime({ offset: true }),
      averageScore: z.number(),
      respondentName: z.string().nullable(),
    }),
  ),
});
export type SurveyAggregate = z.infer<typeof SurveyAggregateSchema>;
